import { useState, useEffect, useLayoutEffect, useRef } from "react";

export const TOUR_KEY = "trat_tour_done";

// Stegen pekar på element med data-tour="…". Finns inte elementet (mobil,
// annan sida, tom lista) visas steget som en ruta mitt på skärmen.
export const TOUR_STEPS = [
  {
    target: null,
    title: { sv: "Välkommen till tRAT", en: "Welcome to tRAT" },
    body: {
      sv: "En kort rundtur genom det viktigaste. Det tar under en minut, och du kan hoppa över när som helst.",
      en: "A short tour of the essentials. It takes less than a minute, and you can skip it at any time.",
    },
  },
  {
    target: "nav-teacher",
    title: { sv: "Skapa en tRAT", en: "Create a tRAT" },
    body: {
      sv: "Importera frågorna från Word, PDF eller Excel, skriv dem direkt här — eller lägg bara in facit om frågorna finns på papper.",
      en: "Import the questions from Word, PDF or Excel, write them here — or just enter the answer key if the questions are on paper.",
    },
  },
  {
    target: "nav-courses",
    title: { sv: "Mina kurser", en: "My courses" },
    body: {
      sv: "Varje kurs samlar sina tRATs, även kollegornas. Koppla en tRAT till en kurs så hittar alla lärare i kursen den.",
      en: "Each course gathers its tRATs, including your colleagues'. Link a tRAT to a course so every teacher on it can find it.",
    },
  },
  {
    target: "nav-questionbank",
    title: { sv: "Frågebanken", en: "Question bank" },
    body: {
      sv: "Spara frågor och återanvänd dem nästa termin. Välj om en fråga är privat eller delas med kursen.",
      en: "Save questions and reuse them next term. Choose whether a question is private or shared with the course.",
    },
  },
  {
    target: "trat-list",
    title: { sv: "Följ tRAT:en live", en: "Follow the tRAT live" },
    body: {
      sv: "Här listas tRATs i dina kurser. Dashboard visar gruppernas poäng, appeals och burning questions medan de skrapar.",
      en: "Your courses' tRATs are listed here. The dashboard shows the groups' scores, appeals and burning questions while they scratch.",
    },
  },
  {
    target: "whats-new",
    title: { sv: "Nytt i tRAT", en: "What's new" },
    body: {
      sv: "När något nytt släpps syns en prick här. Introduktionen kan du starta igen från menyn under ditt namn.",
      en: "A dot appears here when something new is released. You can restart this tour from the menu under your name.",
    },
  },
];

const PAD = 6;     // luft runt det markerade elementet
const GAP = 12;    // avstånd mellan elementet och rutan
const CARD_W = 320;
const DIM = "rgba(0,0,0,0.45)";

function visibleRect(target) {
  if (!target) return null;
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null; // dold (t.ex. sidomenyn på mobil)
  return r;
}

// Rutan till höger om elementet om det får plats, annars under, annars mitt på.
function cardPosition(rect) {
  const vw = window.innerWidth, vh = window.innerHeight;
  if (!rect) return { left: Math.max(16, (vw - CARD_W) / 2), top: Math.max(16, vh * 0.28) };
  if (rect.right + GAP + CARD_W + 16 <= vw) {
    return { left: rect.right + GAP, top: Math.min(Math.max(16, rect.top - 8), vh - 220) };
  }
  const left = Math.min(Math.max(16, rect.left + rect.width / 2 - CARD_W / 2), vw - CARD_W - 16);
  if (rect.bottom + GAP + 200 <= vh) return { left, top: rect.bottom + GAP };
  return { left, top: Math.max(16, rect.top - GAP - 200) };
}

function Tour({ lang, G, onClose, steps = TOUR_STEPS }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const nextRef = useRef(null);
  const sv = lang === "sv";
  const step = steps[i];
  const last = i === steps.length - 1;
  const text = v => v?.[lang] || v?.sv || "";

  useLayoutEffect(() => {
    const el = step.target && document.querySelector(`[data-tour="${step.target}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    const update = () => setRect(visibleRect(step.target));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [step.target]);

  useEffect(() => { nextRef.current?.focus(); }, [i]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") onClose("skipped");
      else if (e.key === "ArrowRight" && !last) setI(n => n + 1);
      else if (e.key === "ArrowLeft" && i > 0) setI(n => n - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [i, last, onClose]);

  const pos = cardPosition(rect);
  const btn = { borderRadius: 8, fontSize: 13, padding: "7px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400 }}>
      {/* Mörkläggning med ett "hål" runt elementet: fyra ytor runt det i
          stället för en jätteskugga, som vissa webbläsare ritar fel */}
      {rect ? (() => {
        const l = rect.left - PAD, t = rect.top - PAD, w = rect.width + PAD * 2, h = rect.height + PAD * 2;
        const dim = { position: "fixed", background: DIM };
        return (
          <div aria-hidden="true">
            <div style={{ ...dim, left: 0, top: 0, right: 0, height: Math.max(0, t) }} />
            <div style={{ ...dim, left: 0, top: t + h, right: 0, bottom: 0 }} />
            <div style={{ ...dim, left: 0, top: t, width: Math.max(0, l), height: h }} />
            <div style={{ ...dim, left: l + w, top: t, right: 0, height: h }} />
            <div style={{ position: "fixed", left: l, top: t, width: w, height: h, borderRadius: 10, outline: `2px solid ${G.gold}`, pointerEvents: "none" }} />
          </div>
        );
      })() : (
        <div aria-hidden="true" style={{ position: "fixed", inset: 0, background: DIM }} />
      )}

      <div role="dialog" aria-modal="true" aria-labelledby="tour-title" style={{
        position: "fixed", left: pos.left, top: pos.top, width: `min(${CARD_W}px, calc(100vw - 32px))`,
        background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "16px 18px",
        boxShadow: "0 12px 40px rgba(0,0,0,0.3)", fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ fontSize: 11, color: G.muted, fontFamily: "'DM Mono', monospace", marginBottom: 6 }}>
          {i + 1} / {steps.length}
        </div>
        <div id="tour-title" style={{ fontSize: 16, fontWeight: 700, color: G.text, marginBottom: 6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{text(step.title)}</div>
        <div style={{ fontSize: 14, color: G.sub, lineHeight: 1.55, marginBottom: 14 }}>{text(step.body)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!last && (
            <button onClick={() => onClose("skipped")} style={{ ...btn, background: "none", border: "none", color: G.muted, padding: "7px 0" }}>
              {sv ? "Hoppa över" : "Skip"}
            </button>
          )}
          <div style={{ flex: 1 }} />
          {i > 0 && (
            <button onClick={() => setI(n => n - 1)} style={{ ...btn, background: "transparent", border: `1px solid ${G.border}`, color: G.text }}>
              {sv ? "Tillbaka" : "Back"}
            </button>
          )}
          <button ref={nextRef} onClick={() => (last ? onClose("done") : setI(n => n + 1))} style={{ ...btn, background: G.gold, border: "none", color: G.onGold, fontWeight: 600 }}>
            {last ? (sv ? "Klar" : "Done") : (sv ? "Nästa" : "Next")}
          </button>
        </div>
      </div>
    </div>
  );
}

export { Tour };
